import {
  Box,
  CtaButton,
  Grid,
  Heading,
  Subtitle,
  TextBody,
} from '@zappyrent/hundop';
import { rem, rgba } from 'polished';
import styled, { css } from 'styled-components';

export const StyledGrid = styled(Grid)`
  flex-direction: column;
  margin: ${rem('16px')} auto;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    margin: ${rem('26px')} auto ${rem('66px')};
  }
`;

export const StyledSection = styled.section`
  margin-top: ${rem('40px')};
  width: 100%;
`;

export const StyledSubSection = styled.div`
  margin-bottom: ${rem('40px')};

  &:last-child {
    margin-bottom: ${rem('16px')};
  }
`;

export const StyledSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    flex-direction: row-reverse;
  }
`;

export const StyledSectionSubContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    min-width: 50%;
    margin-left: ${rem('24px')};
  }
`;

export const StyledContinueButton = styled(CtaButton).attrs({
  tag: 'button',
  user: 'tenant',
})`
  width: 100%;

  text-transform: uppercase;
  margin: 0;

  &:hover {
    color: ${({ theme, variant }) =>
      variant === 'secondary' && theme.colors.tenant.mainAlternate};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.m}) {
    max-width: ${rem('264px')};
  }
`;

export const StyledErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

export const StyledSubtitle = styled(Subtitle)<{
  ref?: React.MutableRefObject<undefined>;
  booking?: boolean;
}>`
  font-size: ${rem('24px')};
  font-weight: 700;
  margin-bottom: ${rem('24px')};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.m}) {
    display: ${({ booking }) => booking && 'none'};
  }
`;

export const StyledSubtitleGreen = styled(TextBody)<{ completed?: boolean }>`
  color: ${({ theme }) => theme.colors.tenant.main};
  font-weight: 600;
  font-size: ${rem('20px')};
  padding-right: ${rem('16px')};
  position: relative;
  display: ${({ completed }) => (completed ? 'inline-block' : 'block')};
  margin-bottom: ${({ completed }) => (completed ? 0 : rem('16px'))};

  &::after {
    ${({ completed, theme }) =>
      completed &&
      css`
        content: '';
        display: block;
        width: ${rem('18px')};
        height: ${rem('10px')};
        border-radius: ${rem('2px')};
        border: ${rem('2px')} solid transparent;
        border-bottom-color: ${theme.colors.cross.greenMint};
        border-left-color: ${theme.colors.cross.greenMint};
        position: absolute;
        top: ${rem('5px')};
        left: 100%;
        transform: rotate(-45deg);
      `}
  }
`;

export const StyledTextBody = styled(TextBody)<{
  black?: boolean;
  right?: boolean;
}>`
  color: ${({ theme, black }) =>
    black ? theme.colors.cross.black : theme.colors.cross.grayson};
  text-transform: ${({ black }) => black && 'capitalize'};
`;

export const StyledList = styled.ul`
  font-weight: bold;
  padding-left: 0;
  margin: ${rem('32px')} 0 0 0;
  color: ${({ theme }) => theme.colors.cross.black};
  list-style: none;

  & p {
    margin: ${rem('8px')} 0 ${rem('24px')} 0;
  }
`;

export const StyledInfo = styled.p`
  font-style: italic;
  margin-top: ${rem('56px')};
`;

export const StyledSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.cross.ultralightGray};
  border-radius: ${rem('16px')};
  padding: ${rem('16px')};
  width: 100%;
  margin-bottom: ${rem('24px')};
`;

export const StyledPriceContainer = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: ${rem('24px')};
  box-shadow: ${({ theme }) =>
    `0 0 ${rem('8px')} ${rgba(theme.colors.cross.silverFox, 0.5)}`};
  margin-bottom: ${rem('16px')};
`;

export const StyledPriceSubContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${rem('24px')} ${rem('16px')};
`;

export const StyledDescriptionList = styled.dl``;

export const StyledImageContainer = styled.div<{ src?: string }>`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    display: block;
    min-width: ${rem('130px')};
    width: ${rem('130px')};
    border-top-left-radius: ${rem('24px')};
    border-bottom-left-radius: ${rem('24px')};
    background-image: ${({ src }) => `url(${src})`};
    background-size: cover;
  }
`;

export const StyledSubContainer = styled.div<{
  amountToPay?: boolean;
  split?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${rem('8px')};
  padding-top: ${({ amountToPay }) => amountToPay && rem('24px')};
  flex-wrap: ${({ split }) => split && 'wrap'};

  & :first-child {
    margin-right: ${rem('8px')};
    margin-bottom: ${({ split }) => split && rem('8px')};
  }

  & :nth-child(2) {
    margin-left: 0;
    text-transform: ${({ split }) => split && 'none'};
  }
`;

export const StyledSemiBoldSubtitle = styled(TextBody)`
  font-weight: 600;
  font-size: ${rem('20px')};
  margin: 0;
`;

export const StyledHeading = styled(Heading)`
  margin-bottom: ${rem('16px')};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.m}) {
    margin-bottom: 0;
  }
`;

export const StyledShowButton = styled.button`
  border: none;
  color: ${({ theme }) => theme.colors.tenant.main};
  text-transform: uppercase;
  background-color: transparent;
  font-size: ${rem('14px')};
  font-weight: 700;
  padding: 0;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.m}) {
    display: none;
  }
`;

export const StyledStickyBox = styled(Box)`
  position: sticky;
  bottom: 0;
  padding: ${rem('16px')} 0;
  background-color: ${({ theme }) => theme.colors.main};
  margin-top: ${rem('64px')};
`;
